import React, { type SyntheticEvent, useState } from "react";
import { useBnbSelector } from "../../../../redux/store";
import { NavStep } from "../../../../../shared-logic/utils/Navigation";
import IconArrowLeft from "../../../../../../../common-deprecated/svg/lexus/arrow-left.svg";
import IconArrowRight from "../../../../../../../common-deprecated/svg/v2/lexus/arrow-right.svg";
import * as StepStyles from "./styles/BuildStepNavigation";
import * as NavigationStyles from "./styles/Navigation";
import Container from "../../../../../../../common-deprecated/styles/v2/lexus/globals/Container";
import { type BuildAndBuyLabelType, BnbLabel } from "../../../../utils/labels";
import { getAemGradesNavHeight } from "../../../../utils/typedUtils";
import {
    isMasterOrSlave,
    isCod,
    isDealer,
    isRTL,
} from "../../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import { isPassiveSummary } from "../../../../utils/settings/bnbSettingUtils";
import { trackNavBackStep, trackNavBackOverview, trackNavMobileOpen } from "../../../../utils/tracking";
import useBackButton from "../../../../hooks/shared/useBackButton";

const stepLabels: Partial<Record<NavStep, BuildAndBuyLabelType>> = {
    // navSteps.submodels label will be overwritten with the model name
    [NavStep.Submodels]: "stepGrade",
    [NavStep.Grades]: "stepGrade",
    [NavStep.Customize]: "stepCustomise",
    [NavStep.Services]: "stepServices",
    [NavStep.Summary]: "stepSummaryAndSave",
    [NavStep.Buy]: "checkoutStep",
    [NavStep.Reserve]: "checkoutStep",
};

type PropsType = {
    children?: React.ReactChild;
    stepNumbers: Partial<Record<NavStep, number>>;
    stepTotal: number;
};

const BuildNavigation = (props: PropsType): JSX.Element | null => {
    const { children, stepNumbers, stepTotal } = props;
    const { step } = useBnbSelector((state) => state.navigation);
    const mobileBasketIsShown = useBnbSelector((state) => state.utils.showMobileBasket);
    const commonSettings = useBnbSelector((state) => state.commonSettings);

    const [navigationOpen, setNavigationOpen] = useState<boolean>(false);
    const toggleNavOpen = (event: SyntheticEvent): void => {
        event.preventDefault();
        if (!navigationOpen) trackNavMobileOpen(step);
        setNavigationOpen(!navigationOpen);
    };

    const fixedHeader = !isMasterOrSlave(commonSettings) && !isCod(commonSettings) && !isDealer(commonSettings);
    const gradeNavHeight = getAemGradesNavHeight();
    const backButton = useBackButton();

    if (isPassiveSummary(commonSettings)) return null;
    return (
        <>
            {fixedHeader && (
                <NavigationStyles.DesktopStickyPadding
                    gradeNavHeight={gradeNavHeight} // Zaidan already adds padding for the main nav, but not the grades nav
                />
            )}
            <NavigationStyles.StickyWrapper
                id="or-build-navigation" // This ID is used in the getSiteNavigationOffset utility
                position={fixedHeader ? "fixed" : "relative"}
                top={fixedHeader ? gradeNavHeight : 0}
                zIndexName={mobileBasketIsShown ? "pageoverlayerExpansion" : "navBar"}
            >
                <NavigationStyles.DesktopNav>
                    <NavigationStyles.NavContainer>
                        <NavigationStyles.NavWrapper id="or-build-navigation-wrapper">
                            <Container position="relative">
                                {!!backButton && !commonSettings.query.codcustomer && (
                                    <NavigationStyles.BackButton
                                        as="a"
                                        href={backButton.url}
                                        onClick={() => {
                                            if (backButton?.step) trackNavBackStep(step);
                                            else trackNavBackOverview(step);
                                        }}
                                    >
                                        {isRTL(commonSettings) ? <IconArrowRight /> : <IconArrowLeft />}
                                    </NavigationStyles.BackButton>
                                )}
                                {children}
                            </Container>
                        </NavigationStyles.NavWrapper>
                    </NavigationStyles.NavContainer>
                </NavigationStyles.DesktopNav>

                <NavigationStyles.MobileNav>
                    <NavigationStyles.MobileNavHeader disabled={mobileBasketIsShown} className="build-mobile-nav">
                        <NavigationStyles.NavContainer>
                            <NavigationStyles.NavWrapper id="or-build-navigation-wrapper-mobile">
                                {!!backButton && !commonSettings.query.codcustomer && (
                                    <NavigationStyles.BackButton
                                        as="a"
                                        href={backButton.url}
                                        onClick={() => {
                                            if (backButton?.step) trackNavBackStep(step);
                                            else trackNavBackOverview(step);
                                        }}
                                    >
                                        {isRTL(commonSettings) ? <IconArrowRight /> : <IconArrowLeft />}
                                    </NavigationStyles.BackButton>
                                )}
                                <NavigationStyles.MobileNavButton
                                    onClick={mobileBasketIsShown ? undefined : toggleNavOpen}
                                >
                                    <StepStyles.Step isActive noPadding>
                                        <NavigationStyles.Badge className="build-badge">
                                            <BnbLabel
                                                label="navigationStepOf"
                                                params={{
                                                    current: stepNumbers[step],
                                                    max: stepTotal,
                                                }}
                                            />
                                        </NavigationStyles.Badge>
                                        <NavigationStyles.StepText>
                                            <BnbLabel label={stepLabels[step]!} />
                                        </NavigationStyles.StepText>
                                    </StepStyles.Step>
                                </NavigationStyles.MobileNavButton>
                            </NavigationStyles.NavWrapper>
                        </NavigationStyles.NavContainer>

                        {/* Element needs to be inside the button so it call full the width of only the button */}
                        <NavigationStyles.FloatingNav visible={navigationOpen}>{children}</NavigationStyles.FloatingNav>
                    </NavigationStyles.MobileNavHeader>
                </NavigationStyles.MobileNav>
            </NavigationStyles.StickyWrapper>
        </>
    );
};

export default BuildNavigation;
